import * as React from 'react';
import {
    Panel, PanelType, Stack, Text, PrimaryButton, DefaultButton, MessageBar,
    MessageBarType, Spinner, SpinnerSize, TextField, Dropdown, IDropdownOption,
    mergeStyles, IconButton, ActionButton, Separator, Dialog, DialogType,
    DialogFooter, Checkbox
} from '@fluentui/react';
import { LanguageContext } from '../../utilities/LocalizationModule';
import {
    StripeProductDto,
    UpsertStripeProductRequest,
    StripeCatalogSyncPreview,
    SyncAddedProduct,
    SyncRenamedProduct
} from '../../data-structures/interfaces';
import {
    listStripeProducts,
    createStripeProduct,
    updateStripeProduct,
    activateStripeProduct,
    deactivateStripeProduct,
    StripeProductMutationResult,
    syncStripeProductsPreview,
    syncStripeProductsApply
} from '../../utilities/helpers/ApiHelper';

interface Props {
    isOpen: boolean;
    onDismiss: () => void;
    // Optional refresh signal: parent can bump this to force a refetch (e.g. on SignalR
    // catalog-changed broadcast from another admin's edit). Any change of value triggers
    // a re-pull; the value itself is opaque.
    refreshNonce?: number;
}

// Internal-product enum labels. Source of truth is Docubird.Entities.Enums.SubscriptionProductEnum
// — these labels mirror the names there so an admin classifying a Stripe product sees the
// same vocabulary they'd find in code / runbooks.
const INTERNAL_PRODUCT_OPTIONS: IDropdownOption[] = [
    { key: 1, text: 'Docubird Professional (1)' },
    { key: 2, text: 'Docubird Essentials (2)' },
    { key: 3, text: 'Service add-on (3)' }
];

const rowCardStyle = mergeStyles({
    border: `1px solid var(--db-border)`,
    borderRadius: 4,
    padding: '10px 12px',
    marginBottom: 8,
    backgroundColor: "var(--db-surface-raised)"
});

const inactiveRowStyle = mergeStyles({
    backgroundColor: "var(--db-surface)",
    color: "var(--db-fg-muted)"
});

const monoStyle = mergeStyles({
    fontFamily: 'Consolas, monospace',
    fontSize: 12
});

// Editor state for the inline create/edit form. `id` is null when creating.
type EditorState = {
    id: number | null;
    stripeProductId: string;
    internalProductId: number | null;
    displayName: string;
    rowVersion?: string;
};

const emptyEditor = (): EditorState => ({
    id: null,
    stripeProductId: '',
    internalProductId: null,
    displayName: '',
    rowVersion: undefined
});

// Editor state for a single new-in-Stripe row inside the sync dialog. The admin must pick
// an internal bucket before the row can be applied; pre-filled display name is editable.
type NewRowDecision = {
    stripeProductId: string;
    stripeName: string;
    // Display-only: the product's live Stripe tax_code, shown so the admin can see Managed
    // Payments readiness while classifying. NOT editable — Stripe owns the tax_code.
    stripeTaxCode?: string | null;
    internalProductId: number | null;
    displayName: string;
    include: boolean;
};

type SyncDialogState = {
    preview: StripeCatalogSyncPreview;
    newDecisions: NewRowDecision[];
    // Renames default to "apply this rename"; admin can opt out per-row.
    renameInclude: Record<string, boolean>;
    renameOverride: Record<string, string>; // override-the-stripe-name editor per row
    // Missing-from-Stripe defaults to NO action; admin opts in to deactivate per-row.
    missingDeactivate: Record<string, boolean>;
};

const ManageStripeProductsPanel: React.FC<Props> = ({ isOpen, onDismiss, refreshNonce }) => {
    const languageStrings = React.useContext(LanguageContext);
    const [products, setProducts] = React.useState<StripeProductDto[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isBusy, setIsBusy] = React.useState(false);
    const [editor, setEditor] = React.useState<EditorState | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [warningMessage, setWarningMessage] = React.useState<string | null>(null);
    const [syncState, setSyncState] = React.useState<SyncDialogState | null>(null);
    const [isSyncing, setIsSyncing] = React.useState(false);
    const [isSyncDialogBusy, setIsSyncDialogBusy] = React.useState(false);

    const refetch = React.useCallback(async () => {
        setIsLoading(true);
        try {
            const rows = await listStripeProducts();
            setProducts(rows);
        } catch (err: any) {
            // `listStripeProducts` swallows errors via ErrorHelper and returns []. We catch
            // here only as a belt-and-suspenders against a future change that re-throws —
            // without this the rejection would become an unhandled-promise warning and the
            // panel would silently show an empty list with no indication why.
            const body = err?.response?.data;
            const fromBody = (typeof body === 'string' ? body : body?.error) as string | undefined;
            setErrorMessage(fromBody ?? err?.message ?? 'Failed to load Stripe product catalog.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (!isOpen) return;
        // Fresh open or external refresh signal — pull the catalog from the server. Closes
        // the gap where another admin's edit lands during the panel's lifetime.
        refetch();
    }, [isOpen, refreshNonce, refetch]);

    const handleResult = (result: StripeProductMutationResult) => {
        setErrorMessage(null);
        setWarningMessage(result.warning ?? null);
        setEditor(null);
        // Refresh from server — keeps row versions in sync, single source of truth.
        refetch();
    };

    const handleApiError = (err: any) => {
        const body = err?.response?.data;
        const fromBody = (typeof body === 'string' ? body : body?.error) as string | undefined;
        setErrorMessage(fromBody ?? err?.message ?? 'Request failed.');
        setWarningMessage(null);
    };

    const submitEditor = async () => {
        if (!editor) return;
        if (!editor.stripeProductId.trim() || !editor.displayName.trim() || editor.internalProductId == null) {
            setErrorMessage('All fields are required.');
            return;
        }
        const body: UpsertStripeProductRequest = {
            stripeProductId: editor.stripeProductId.trim(),
            internalProductId: editor.internalProductId,
            displayName: editor.displayName.trim(),
            rowVersion: editor.rowVersion
        };
        setIsBusy(true);
        try {
            const result = editor.id == null
                ? await createStripeProduct(body)
                : await updateStripeProduct(editor.id, body);
            handleResult(result);
        } catch (err) {
            handleApiError(err);
        } finally {
            setIsBusy(false);
        }
    };

    const setActive = async (row: StripeProductDto, active: boolean) => {
        setIsBusy(true);
        try {
            const result = active
                ? await activateStripeProduct(row.id)
                : await deactivateStripeProduct(row.id);
            handleResult(result);
        } catch (err) {
            handleApiError(err);
        } finally {
            setIsBusy(false);
        }
    };

    const startEdit = (row: StripeProductDto) => {
        setErrorMessage(null);
        setWarningMessage(null);
        setEditor({
            id: row.id,
            stripeProductId: row.stripeProductId,
            internalProductId: row.internalProductId,
            displayName: row.displayName,
            rowVersion: row.rowVersion
        });
    };

    const startCreate = () => {
        setErrorMessage(null);
        setWarningMessage(null);
        setEditor(emptyEditor());
    };

    // Builds the sync-dialog state from a preview payload. Pre-populates the decision
    // arrays so the UI can drive single-source-of-truth rendering without reaching back
    // into the preview.
    const initSyncStateFromPreview = (preview: StripeCatalogSyncPreview): SyncDialogState => ({
        preview,
        newDecisions: preview.newInStripe.map(p => ({
            stripeProductId: p.stripeProductId,
            stripeName: p.stripeName,
            stripeTaxCode: p.stripeTaxCode ?? null,
            internalProductId: null,
            displayName: p.suggestedDisplayName,
            include: true
        })),
        renameInclude: Object.fromEntries(preview.renamedInStripe.map(r => [r.stripeProductId, true])),
        renameOverride: Object.fromEntries(preview.renamedInStripe.map(r => [r.stripeProductId, r.stripeName])),
        missingDeactivate: Object.fromEntries(preview.missingFromStripe.map(m => [m.stripeProductId, false])),
    });

    const startSync = async () => {
        setErrorMessage(null);
        setWarningMessage(null);
        setIsSyncing(true);
        try {
            const preview = await syncStripeProductsPreview();
            setSyncState(initSyncStateFromPreview(preview));
        } catch (err) {
            handleApiError(err);
        } finally {
            setIsSyncing(false);
        }
    };

    const applySync = async () => {
        if (!syncState) return;

        // Build the apply request from the decision state. Skip new rows that the admin
        // explicitly excluded or hasn't classified (an unclassified row is a missing
        // internalProductId — server would reject anyway, but we short-circuit here for
        // a cleaner error.)
        const addedProducts: SyncAddedProduct[] = syncState.newDecisions
            .filter(d => d.include && d.internalProductId != null && d.displayName.trim().length > 0)
            .map(d => ({
                stripeProductId: d.stripeProductId,
                internalProductId: d.internalProductId!,
                displayName: d.displayName.trim()
            }));

        const unclassified = syncState.newDecisions.filter(d => d.include && d.internalProductId == null);
        if (unclassified.length > 0) {
            setErrorMessage(
                `${unclassified.length} new product(s) need an internal bucket assigned before sync can apply.`
            );
            return;
        }

        const renamedProducts: SyncRenamedProduct[] = syncState.preview.renamedInStripe
            .filter(r => syncState.renameInclude[r.stripeProductId])
            .map(r => ({
                stripeProductId: r.stripeProductId,
                newDisplayName: (syncState.renameOverride[r.stripeProductId] ?? r.stripeName).trim()
            }));

        const deactivatedProducts: string[] = syncState.preview.missingFromStripe
            .filter(m => syncState.missingDeactivate[m.stripeProductId])
            .map(m => m.stripeProductId);

        setIsSyncDialogBusy(true);
        setErrorMessage(null);
        try {
            const outcome = await syncStripeProductsApply({
                stripeListSnapshot: syncState.preview.stripeListSnapshot,
                addedProducts,
                renamedProducts,
                deactivatedProducts,
            });
            if (outcome.kind === "ok") {
                setSyncState(null);
                // Refetch rather than trusting outcome.products: the apply response no longer
                // carries tax codes (they're enriched live from Stripe by the list endpoint, not
                // stored), so using it directly would flash every badge to "not set". refetch()
                // re-pulls the enriched list — same path handleResult uses after create/update.
                refetch();
                setWarningMessage(
                    `Sync applied: ${addedProducts.length} added, ${renamedProducts.length} renamed, ${deactivatedProducts.length} deactivated.`
                );
            } else if (outcome.kind === "conflict") {
                // Re-seed dialog state from the fresh preview so the admin can re-decide.
                setSyncState(initSyncStateFromPreview(outcome.freshPreview));
                setErrorMessage(outcome.error);
            } else {
                setErrorMessage(outcome.error);
            }
        } catch (err) {
            handleApiError(err);
        } finally {
            setIsSyncDialogBusy(false);
        }
    };

    const renderSyncDialog = () => {
        if (!syncState) return null;
        const summary = `${syncState.preview.newInStripe.length} new • ${syncState.preview.renamedInStripe.length} renamed • ${syncState.preview.missingFromStripe.length} missing locally`;

        // What the admin has actually selected to write — this is what Apply will do.
        // Mirrors the filtering inside applySync(); kept in sync by hand because a shared
        // selector would obscure the per-section unclassified vs. classified distinction.
        const pendingAdds = syncState.newDecisions
            .filter(d => d.include && d.internalProductId != null && d.displayName.trim().length > 0).length;
        const unclassifiedAdds = syncState.newDecisions
            .filter(d => d.include && d.internalProductId == null).length;
        const pendingRenames = syncState.preview.renamedInStripe
            .filter(r => syncState.renameInclude[r.stripeProductId]).length;
        const pendingDeactivates = syncState.preview.missingFromStripe
            .filter(m => syncState.missingDeactivate[m.stripeProductId]).length;
        const totalPending = pendingAdds + pendingRenames + pendingDeactivates;

        // Build a natural-language summary so the admin sees what Apply will actually
        // write before clicking. Falls back to "no changes" when nothing is ticked.
        const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
        const parts: string[] = [];
        if (pendingAdds > 0) parts.push(`register ${plural(pendingAdds, 'new product', 'new products')}`);
        if (pendingRenames > 0) parts.push(`rename ${plural(pendingRenames, 'product', 'products')}`);
        if (pendingDeactivates > 0) parts.push(`deactivate ${plural(pendingDeactivates, 'product', 'products')}`);
        // Oxford-style join: "A", "A and B", "A, B and C".
        const joinedParts = parts.length <= 1
            ? (parts[0] ?? '')
            : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
        const pendingSummary = totalPending === 0
            ? 'No changes selected — the local product catalog will remain unchanged.'
            : `Apply will ${joinedParts} in the local product catalog to match Stripe.`;
        return (
            <Dialog
                hidden={false}
                onDismiss={() => isSyncDialogBusy ? undefined : setSyncState(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Sync Stripe products',
                    subText: summary
                }}
                minWidth={760}
                maxWidth={760}
                modalProps={{
                    isBlocking: true,
                    styles: {
                        main: {
                            // Fluent's default Dialog caps width inside a media-query
                            // selector — overriding maxWidth/width at the top level alone
                            // is silently lost. Match the selector to actually widen it.
                            selectors: {
                                '@media (min-width: 480px)': {
                                    width: 760,
                                    minWidth: 760,
                                    maxWidth: 760
                                }
                            }
                        }
                    }
                }}
            >
                <Stack tokens={{ childrenGap: 12 }} styles={{ root: { maxHeight: '60vh', overflowY: 'auto', paddingRight: 4 } }}>
                    {/* New in Stripe — require internal bucket per row */}
                    <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>New in Stripe ({syncState.newDecisions.length})</Text>
                        {syncState.newDecisions.length === 0 && (
                            <Text variant="small" styles={{ root: { color: "var(--db-fg-muted)" } }}>
                                Nothing new — Stripe matches the local catalog.
                            </Text>
                        )}
                        {syncState.newDecisions.map((d, idx) => (
                            <Stack key={d.stripeProductId} tokens={{ childrenGap: 4 }} className={rowCardStyle}>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                    <Checkbox
                                        checked={d.include}
                                        onChange={(_, v) => setSyncState({
                                            ...syncState,
                                            newDecisions: syncState.newDecisions.map((x, i) => i === idx ? { ...x, include: !!v } : x)
                                        })}
                                        disabled={isSyncDialogBusy}
                                    />
                                    <Stack styles={{ root: { flexGrow: 1 } }}>
                                         <Text className={monoStyle}>{d.stripeProductId}</Text>
                                         <Text variant="small" styles={{ root: { color: "var(--db-fg-muted)" } }}>Stripe name: {d.stripeName}</Text>
                                         <Text variant="small" styles={{ root: { color: "var(--db-fg-muted)" } }}>Stripe tax code: {d.stripeTaxCode || 'not set'}</Text>
                                     </Stack>
                                 </Stack>
                                 {d.include && (
                                     <Stack horizontal tokens={{ childrenGap: 8 }}>
                                        <Dropdown
                                            label="Internal bucket"
                                            options={INTERNAL_PRODUCT_OPTIONS}
                                            selectedKey={d.internalProductId ?? undefined}
                                            onChange={(_, opt) => setSyncState({
                                                ...syncState,
                                                newDecisions: syncState.newDecisions.map((x, i) => i === idx ? { ...x, internalProductId: opt ? Number(opt.key) : null } : x)
                                            })}
                                            styles={{ root: { flexBasis: 250 } }}
                                            disabled={isSyncDialogBusy}
                                        />
                                        <TextField
                                            label="Display name"
                                            value={d.displayName}
                                            onChange={(_, v) => setSyncState({
                                                ...syncState,
                                                newDecisions: syncState.newDecisions.map((x, i) => i === idx ? { ...x, displayName: v ?? '' } : x)
                                            })}
                                             styles={{ root: { flexGrow: 1 } }}
                                             disabled={isSyncDialogBusy}
                                         />
                                     </Stack>
                                 )}
                             </Stack>
                         ))}
                    </Stack>

                    {/* Renamed in Stripe */}
                    <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Renamed in Stripe ({syncState.preview.renamedInStripe.length})</Text>
                        {syncState.preview.renamedInStripe.map(r => (
                            <Stack key={r.stripeProductId} horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} className={rowCardStyle}>
                                <Checkbox
                                    checked={!!syncState.renameInclude[r.stripeProductId]}
                                    onChange={(_, v) => setSyncState({
                                        ...syncState,
                                        renameInclude: { ...syncState.renameInclude, [r.stripeProductId]: !!v }
                                    })}
                                    disabled={isSyncDialogBusy}
                                />
                                <Stack styles={{ root: { flexGrow: 1 } }}>
                                    <Text className={monoStyle}>{r.stripeProductId}</Text>
                                    <Text variant="small">{r.localDisplayName} → {r.stripeName}</Text>
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>

                    {/* Missing from Stripe — opt-in deactivate */}
                    <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Missing from Stripe ({syncState.preview.missingFromStripe.length})</Text>
                        {syncState.preview.missingFromStripe.length > 0 && (
                            <Text variant="small" styles={{ root: { color: "var(--db-fg-muted)" } }}>
                                Tick to deactivate locally. Defaults to no action so a transient Stripe archive doesn't retire a product accidentally.
                            </Text>
                        )}
                        {syncState.preview.missingFromStripe.map(m => (
                            <Stack key={m.stripeProductId} horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} className={rowCardStyle}>
                                <Checkbox
                                    label="Deactivate"
                                    checked={!!syncState.missingDeactivate[m.stripeProductId]}
                                    onChange={(_, v) => setSyncState({
                                        ...syncState,
                                        missingDeactivate: { ...syncState.missingDeactivate, [m.stripeProductId]: !!v }
                                    })}
                                    disabled={isSyncDialogBusy}
                                />
                                <Stack styles={{ root: { flexGrow: 1 } }}>
                                    <Text className={monoStyle}>{m.stripeProductId}</Text>
                                    <Text variant="small">{m.localDisplayName}</Text>
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
                <Stack tokens={{ childrenGap: 6 }} styles={{ root: { marginTop: 12 } }}>
                    <MessageBar messageBarType={MessageBarType.info}>
                        {pendingSummary}
                    </MessageBar>
                    {unclassifiedAdds > 0 && (
                        <MessageBar messageBarType={MessageBarType.warning}>
                            {unclassifiedAdds} new product(s) still need an internal bucket before they can be added.
                        </MessageBar>
                    )}
                </Stack>
                <DialogFooter>
                    <DefaultButton onClick={() => setSyncState(null)} text="Cancel" disabled={isSyncDialogBusy} />
                    <PrimaryButton
                        onClick={applySync}
                        text={isSyncDialogBusy ? 'Applying…' : 'Apply'}
                        disabled={isSyncDialogBusy || totalPending === 0 || unclassifiedAdds > 0}
                    />
                </DialogFooter>
            </Dialog>
        );
    };

    const renderEditor = () => {
        if (!editor) return null;
        return (
            <Stack tokens={{ childrenGap: 8 }} className={rowCardStyle}>
                <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
                    {editor.id == null ? (languageStrings.AddProduct ?? 'Add product') : (languageStrings.EditProduct ?? 'Edit product')}
                </Text>
                <TextField
                    label="Stripe Product ID"
                    placeholder="prod_…"
                    value={editor.stripeProductId}
                    onChange={(_, v) => setEditor({ ...editor, stripeProductId: v ?? '' })}
                    disabled={isBusy}
                />
                <Dropdown
                    label="Internal product bucket"
                    options={INTERNAL_PRODUCT_OPTIONS}
                    selectedKey={editor.internalProductId ?? undefined}
                    onChange={(_, opt) => setEditor({ ...editor, internalProductId: opt ? Number(opt.key) : null })}
                    disabled={isBusy}
                />
                <TextField
                    label="Display name"
                    value={editor.displayName}
                    onChange={(_, v) => setEditor({ ...editor, displayName: v ?? '' })}
                    disabled={isBusy}
                />
                <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
                    <DefaultButton text={languageStrings.Cancel ?? 'Cancel'} onClick={() => setEditor(null)} disabled={isBusy} />
                    <PrimaryButton text={languageStrings.Save ?? 'Save'} onClick={submitEditor} disabled={isBusy} />
                </Stack>
            </Stack>
        );
    };

    const renderRow = (row: StripeProductDto) => {
        const isEditing = editor?.id === row.id;
        if (isEditing) return null; // editor is rendered separately above
        return (
            <Stack key={row.id} horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} className={`${rowCardStyle} ${row.isActive ? '' : inactiveRowStyle}`}>
                <Stack tokens={{ childrenGap: 2 }} styles={{ root: { flexGrow: 1, minWidth: 0 } }}>
                    <Text styles={{ root: { fontWeight: 600 } }}>{row.displayName}</Text>
                    <Text className={monoStyle}>{row.stripeProductId}</Text>
                    <Text variant="small" styles={{ root: { color: "var(--db-fg-muted)" } }}>
                        Internal bucket: {INTERNAL_PRODUCT_OPTIONS.find(o => o.key === row.internalProductId)?.text ?? row.internalProductId}
                        {row.isActive ? '' : '  •  inactive'}
                    </Text>
                    <Text variant="small" styles={{ root: { color: row.taxCode ? "var(--db-fg-muted)" : "var(--db-error-fg)" } }}>
                        Tax code: {row.taxCode || 'not set'}
                    </Text>
                </Stack>
                <IconButton iconProps={{ iconName: 'Edit' }} title="Edit" ariaLabel="Edit" disabled={isBusy} onClick={() => startEdit(row)} />
                {row.isActive
                    ? <IconButton iconProps={{ iconName: 'Blocked2' }} title="Deactivate" ariaLabel="Deactivate" disabled={isBusy} onClick={() => setActive(row, false)} />
                    : <IconButton iconProps={{ iconName: 'CheckMark' }} title="Activate" ariaLabel="Activate" disabled={isBusy} onClick={() => setActive(row, true)} />}
            </Stack>
        );
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            headerText={languageStrings.ManageStripeProducts ?? 'Manage Stripe products'}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            isFooterAtBottom
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
                    <DefaultButton text={languageStrings.Close ?? 'Close'} onClick={onDismiss} />
                </Stack>
            )}
        >
            <Stack tokens={{ childrenGap: 8 }}>
                {errorMessage && (
                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage(null)}>
                        {errorMessage}
                    </MessageBar>
                )}
                {warningMessage && (
                    <MessageBar messageBarType={MessageBarType.warning} onDismiss={() => setWarningMessage(null)}>
                        {warningMessage}
                    </MessageBar>
                )}

                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Text variant="medium" styles={{ root: { color: "var(--db-fg-muted)" } }}>
                        {products.length} product(s) registered
                    </Text>
                    <Stack horizontal tokens={{ childrenGap: 4 }}>
                        <ActionButton
                            iconProps={{ iconName: 'Sync' }}
                            onClick={startSync}
                            disabled={isBusy || editor != null || isSyncing || syncState != null}>
                            {isSyncing ? 'Loading preview…' : (languageStrings.SyncFromStripe ?? 'Sync from Stripe')}
                        </ActionButton>
                        <ActionButton iconProps={{ iconName: 'Add' }} onClick={startCreate} disabled={isBusy || editor != null}>
                            {languageStrings.AddProduct ?? 'Add product'}
                        </ActionButton>
                    </Stack>
                </Stack>

                {renderEditor()}

                {isLoading
                    ? <Spinner size={SpinnerSize.medium} label="Loading…" />
                    : products.length === 0
                        ? <MessageBar>No Stripe products registered yet.</MessageBar>
                        : <Stack>{products.map(renderRow)}</Stack>}

                <Separator />
                <Text variant="small" styles={{ root: { color: "var(--db-fg-muted)" } }}>
                    Deactivating a product hides it from the Add Subscription dropdown but does not affect
                    existing subscriptions. Hard-delete is not available; use deactivate instead.
                </Text>
            </Stack>
            {renderSyncDialog()}
        </Panel>
    );
};

export default ManageStripeProductsPanel;
